import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { EntityFinancialStats, EntityStatsTransaction } from '../../lib/entityStatsApi';
import { formatCurrency, formatDate } from '../../lib/format';
import { useI18n } from '../../contexts/I18nContext';
import { CHART_TOOLTIP_LABEL_STYLE, CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// ---------------------------------------------------------------------------
// Shared "Statistici financiare" section for the supplier / client /
// subscription detail pages: KPI cards + 12-month bar chart + the associated
// transactions list. Data: GET /api/<entity>/:id/stats via useEntityStats.
// ---------------------------------------------------------------------------

interface FinancialStatsSectionProps {
  stats: EntityFinancialStats | undefined;
  isLoading: boolean;
  // Money flow relative to the firm: 'in' (client payments) or 'out'
  // (supplier/subscription payments). Only affects copy, not math.
  direction?: 'in' | 'out';
}

const SOURCE_BADGES: Record<
  EntityStatsTransaction['source'],
  { label: string; className: string }
> = {
  bank: { label: 'Bancă', className: 'bg-blue-100 text-blue-700' },
  invoice: { label: 'Factură', className: 'bg-purple-100 text-purple-700' },
  smartbill: { label: 'SmartBill', className: 'bg-emerald-100 text-emerald-700' },
};

// 'YYYY-MM' -> 'mai 25' (ro-RO short month + 2-digit year).
function monthLabel(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  return new Intl.DateTimeFormat('ro-RO', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(d);
}

function fmtAmount(value: number, currency: string): string {
  return `${value.toLocaleString('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

export function FinancialStatsSection({
  stats,
  isLoading,
  direction = 'out',
}: FinancialStatsSectionProps) {
  const { t } = useI18n();

  const chartData = useMemo(
    () =>
      (stats?.monthly ?? []).map((m) => ({
        ...m,
        label: monthLabel(m.month),
      })),
    [stats?.monthly],
  );

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-lg border border-gray-200 bg-gray-50" />
          ))}
        </div>
        <div className="h-48 rounded-lg border border-gray-200 bg-gray-50" />
      </div>
    );
  }
  if (!stats) return null;

  const totalLabel = direction === 'in' ? 'Total încasat' : 'Total plătit';
  const lastLabel = direction === 'in' ? 'Ultima încasare' : 'Ultima plată';

  return (
    <div className="space-y-4">
      {/* KPIs. Phones: rows of one card (label left, figure right) instead of
          three tall cards before the chart. */}
      <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white text-sm shadow-sm sm:hidden">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <dt className="text-gray-500">{totalLabel}</dt>
          <dd className="text-right">
            <span className="font-semibold tabular-nums text-gray-900">
              {fmtAmount(stats.totals.totalAmount, stats.currency)}
            </span>
            <span className="block text-xs text-gray-400">
              {stats.totals.transactionCount}{' '}
              {stats.totals.transactionCount === 1 ? 'tranzacție' : 'tranzacții'}
            </span>
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <dt className="text-gray-500">Medie lunară (12 luni)</dt>
          <dd className="font-semibold tabular-nums text-gray-900">
            {fmtAmount(stats.totals.monthlyAverage, stats.currency)}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <dt className="text-gray-500">{lastLabel}</dt>
          <dd className="text-right">
            {stats.totals.lastPayment ? (
              <>
                <span className="font-semibold tabular-nums text-gray-900">
                  {fmtAmount(stats.totals.lastPayment.amount, stats.totals.lastPayment.currency)}
                </span>
                <span className="block text-xs text-gray-400">
                  {formatDate(stats.totals.lastPayment.date)}
                </span>
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </dd>
        </div>
      </dl>
      <div className="hidden gap-3 sm:grid sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">{totalLabel}</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {fmtAmount(stats.totals.totalAmount, stats.currency)}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            {stats.totals.transactionCount}{' '}
            {stats.totals.transactionCount === 1 ? 'tranzacție' : 'tranzacții'}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">
            Medie lunară (12 luni)
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {fmtAmount(stats.totals.monthlyAverage, stats.currency)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">{lastLabel}</p>
          {stats.totals.lastPayment ? (
            <>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {fmtAmount(stats.totals.lastPayment.amount, stats.totals.lastPayment.currency)}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {formatDate(stats.totals.lastPayment.date)}
              </p>
            </>
          ) : (
            <p className="mt-1 text-lg font-semibold text-gray-400">—</p>
          )}
        </div>
      </div>

      {/* 12-month chart */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <p className="mb-2 text-sm font-medium text-gray-700">Evoluție lunară ({stats.currency})</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={false}
                tickLine={false}
                width={56}
                tickFormatter={(v: number) => formatCurrency(v, stats.currency)}
              />
              <Tooltip
                formatter={(value) => [fmtAmount(Number(value), stats.currency), 'Sumă']}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                contentStyle={CHART_TOOLTIP_STYLE}
              />
              <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Transactions */}
      {stats.transactions.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-gray-500 shadow-sm">
          Nicio tranzacție asociată.
        </p>
      ) : (
        <>
          {/* Below lg: one card per transaction instead of a six-column table
              that scrolled sideways inside the page. */}
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white lg:hidden">
            {stats.transactions.map((tx) => {
              const badge = SOURCE_BADGES[tx.source];
              return (
                <li key={`${tx.source}-${tx.id}`} className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0 truncate text-sm text-gray-900">
                      {tx.description ?? tx.invoiceNumber ?? '—'}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                      {fmtAmount(tx.amount, tx.currency)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                    <span>{formatDate(tx.date)}</span>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 font-medium ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                    {tx.paymentMethod && <span>{t(`paymentMethod.${tx.paymentMethod}`)}</span>}
                    {tx.status && (
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 font-medium ${
                          tx.status === 'PAID'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {tx.status === 'PAID' ? 'Plătit' : tx.status}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Data</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Descriere</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Sursă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Metodă</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Sumă</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {stats.transactions.map((tx) => {
                  const badge = SOURCE_BADGES[tx.source];
                  return (
                    <tr key={`${tx.source}-${tx.id}`} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {formatDate(tx.date)}
                      </td>
                      <td
                        className="max-w-xs truncate px-3 py-2 text-gray-900"
                        title={tx.description ?? undefined}
                      >
                        {tx.description ?? tx.invoiceNumber ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {tx.paymentMethod ? t(`paymentMethod.${tx.paymentMethod}`) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900">
                        {fmtAmount(tx.amount, tx.currency)}
                      </td>
                      <td className="px-3 py-2">
                        {tx.status ? (
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                              tx.status === 'PAID'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {tx.status === 'PAID' ? 'Plătit' : tx.status}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
